import React, { useEffect, useMemo, useState } from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { getNonEmptyFields } from 'lib/context/MyScribeAI/EnhanceNoteContext/lib/helpers';
import { useChatContextApi } from 'lib/context/MyScribeAI/MyScribeTemplateContext/ChatContext/ChatContext';

import { getTypeOfNotes } from 'components/practice/charts/ClinicalNote/MyScribeAI/lib/helpers';
import {
  activePromptConversationsState,
  intelligenceChatIdState,
  isAIGeneratingState,
  myScribeAILoadingState,
  myScribeAutoMapSelectedNoteTypeState
} from 'components/practice/charts/ClinicalNote/MyScribeAI/state';
import AIChatInput from 'components/shared/AIChatInput/AIChatInput';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Transcribe from 'components/shared/Transcribe/Transcribe';
import { clinicalNote as clinicalNoteState, permissions } from 'components/state';

import useChatInput from '../../hooks/useChatInput';
import useComponentAdd from '../../hooks/useComponentAdd';
import useNoteType from '../../hooks/useNoteType';
import { stopResponseGeneration } from '../../lib/helpers';

import ComponentButtons from './ComponentButtons';
import { MiddleAlert } from './MiddleAlert';
import { getFormsFinalNarrative } from './lib/helpers';

export function ChatFooter() {
  const { selectedPromptNoteType } = useNoteType();
  const { inputTags: tags, onTagRemove, onTagPreview } = useComponentAdd();
  const { inputValue, disableGenerate, onTextChange } = useChatInput();
  const { onEnhanceNote } = useChatContextApi();
  const [isGenerating, setIsGenerating] = useRecoilState(isAIGeneratingState);
  const [isLoading, setIsLoading] = useRecoilState(myScribeAILoadingState);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showFilledEntriesAlert, setShowFilledEntriesAlert] = useState(false);
  const [isFilledFromEntriesOrIgnored, setIsFilledFromEntriesOrIgnored] = useState(false);
  const [inputTags, setInputTags] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [files, setFiles] = useState([]);
  const intelligenceChatId = useRecoilValue(intelligenceChatIdState);
  const [activePromptConversations, setActivePromptConversations] = useRecoilState(
    activePromptConversationsState
  );

  const clinicalNote = useRecoilValue(clinicalNoteState);
  const preSelectedNoteType = useRecoilValue(myScribeAutoMapSelectedNoteTypeState);
  const selectedNoteType = (selectedPromptNoteType || preSelectedNoteType)?.type;
  const { is_admin } = useRecoilValue(permissions);

  const {
    currentHpOverviewData,
    overviewData,
    cnDisplaySettings,
    advancedHP,
    advancedSOAP,
    customFormTypes
  } = useClinicalNoteContext() || {};
  const {
    medicationHistory,
    surgicalHistory,
    weightLossAdministration,
    familyHistory,
    socialHistory,
    vaccines,
    allergies
  } = overviewData || {};
  const aggregatedOverviewData = useMemo(() => {
    return {
      medicationHistory,
      surgicalHistory,
      weightLossAdministration,
      familyHistory,
      socialHistory,
      vaccines,
      allergies
    };
  }, []);

  const { components_order: _, ...clinicalNoteForms } = useMemo(
    () => ({ ...clinicalNote, ...currentHpOverviewData, ...aggregatedOverviewData }),
    [clinicalNote, currentHpOverviewData, aggregatedOverviewData]
  );

  const nonEmptyFields = useMemo(
    () =>
      getNonEmptyFields({
        ...clinicalNoteForms,
        ...(clinicalNoteForms?.['mha'] || {}),
        ...(clinicalNoteForms?.['epn'] || {})
      }),
    [clinicalNoteForms]
  );
  const nonEmptyTitles = useMemo(() => Object.keys(nonEmptyFields || {}), [nonEmptyFields]);

  const allFormTypes = getTypeOfNotes({
    cnDisplaySettings,
    advancedHP,
    advancedSOAP,
    customFormTypes
  });

  const finalNonEmptyTitles = useMemo(() => {
    const components = allFormTypes?.[selectedNoteType]?.components || {};
    return nonEmptyTitles.filter((title) => {
      return Object.keys(components).includes(title.toLowerCase());
    });
  }, [nonEmptyTitles, allFormTypes, selectedNoteType]);

  useEffect(() => {
    if (isFilledFromEntriesOrIgnored) {
      return;
    }
    setShowFilledEntriesAlert(finalNonEmptyTitles.length > 0);
  }, [finalNonEmptyTitles]);

  useEffect(() => {
    setInputTags(tags);
  }, [tags]);

  const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;
  const wordCount = countWords(inputValue);

  const isWordCountLow = wordCount < 50;

  const MAX_TOTAL_SIZE = 50 * 1024 * 1000; // 50MB in bytes

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    // Calculate current total size of existing files
    const currentSize = files.reduce((sum, file) => sum + file.size, 0);

    // Calculate total size of new files
    const newFilesSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

    // Check if adding new files would exceed total size limit
    if (currentSize + newFilesSize > MAX_TOTAL_SIZE) {
      showAlert({
        color: 'danger',
        message:
          'Unable to upload: The total file size exceeds the 50MB limit. Please select smaller files or reduce the number of files to stay within the limit.'
      });
      e.target.value = '';
      return;
    }

    // Append new files, ensuring no duplicates by name
    setFiles((prev) => {
      const existingNames = new Set(prev.map((file) => file.name));
      const newFiles = selectedFiles.filter((file) => !existingNames.has(file.name));
      return [...prev, ...newFiles];
    });

    e.target.value = '';
  };

  const handleRemoveFile = (fileName) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const convertFilesToBase64 = async () => {
    const attachments = [];

    for (const file of files) {
      try {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        attachments.push({
          name: file.name,
          type: file.type,
          data: base64Data,
          url: `data:${file.type};base64, ${base64Data}`
        });
      } catch (error) {
        console.error(`Failed to process ${file.name}: ${error.message}`);
        return [];
      }
    }

    return attachments;
  };

  const handleGenerate = async () => {
    if (!inputValue?.length && !files.length) {
      setUploadError('Please provide a prompt or upload at least one file.');
      return;
    }

    if (isWordCountLow && !files.length && !intelligenceChatId) {
      setShowConfirm(true);
      return;
    }

    await onGenerate();
  };

  const onGenerate = async () => {
    const attachments = await convertFilesToBase64();
    if (uploadError) return;

    setActivePromptConversations((prevPrompts = []) => [
      ...(prevPrompts || []),
      { id: self.crypto.randomUUID(), message: inputValue, agent: 'user', attachments }
    ]);
    setIsLoading(true);
    onTextChange({ target: { value: '' } });
    setInputTags([]);
    setFiles([]);
    setUploadError(null);

    await onEnhanceNote({
      userMessage: {
        message: inputValue,
        tags: inputTags?.map((item) => item.id),
        agent: 'user',
        attachments
      },
      intelligenceChatId,
      noteType: selectedPromptNoteType?.type,
      isFollowUp: activePromptConversations?.length > 1
    });
  };

  const onStopGeneration = () => {
    stopResponseGeneration({ intelligenceChatId });
    setIsGenerating(false);
    setIsLoading(false);
  };

  const onInclude = () => {
    const finalNarrative = getFormsFinalNarrative({
      titles: finalNonEmptyTitles,
      clinicalNoteForms,
      type: selectedNoteType
    });
    onTextChange({ target: { value: finalNarrative } });
    setIsFilledFromEntriesOrIgnored(true);
    setShowFilledEntriesAlert(false);
  };

  const onIgnore = () => {
    setShowFilledEntriesAlert(false);
    setIsFilledFromEntriesOrIgnored(true);
  };

  return (
    <div className="flex flex-col gap-4">
      {showFilledEntriesAlert && !intelligenceChatId && !activePromptConversations?.length && (
        <MiddleAlert
          icon={<Icon icon="info-circle-filled" size={20} className="text-yellow-600" />}
          footer={
            <div className="mt-2 flex space-x-3">
              <Button text="Include Entries" className="bg-warning-500" onClick={onInclude} />
              <Button text="Ignore" type="transparent" onClick={onIgnore} />
            </div>
          }
          type="warning"
          title="We noticed that there are some sections already filled on the clinical note!"
          message="Would you like to include this information in your prompt? MyScribe AI will generate a complete note and map it back to the appropriate sections. If you choose not to include your existing entries, they may be overwritten."
        />
      )}
      {uploadError && (
        <MiddleAlert
          icon={<Icon icon="warning-circle" size={20} className="text-red-600" />}
          type="error"
          title="Upload Error"
          message={uploadError}
          footer={<Button text="Dismiss" type="transparent" onClick={() => setUploadError(null)} />}
        />
      )}
      <>
        <AIChatInput
          isHTML
          className="mb-3 w-full max-w-[756px] border-2 !border-neutral-200 px-0 shadow-none outline outline-1 outline-neutral-200 xl:w-[756px]"
          leftButtons={[]}
          rightButtons={[]}
          onTextChange={onTextChange}
          onSubmit={handleGenerate}
          placeholder={
            intelligenceChatId
              ? 'Chat with MyScribe to make edits or ask relevant questions!'
              : 'Patient presents with…'
          }
          value={inputValue}
          inputClassName="max-h-[192px] min-h-[32px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {is_admin && (
                <div key="upload" className="flex items-center">
                  <Tippy className="tippy-dark" content="Upload files" placement="top">
                    <div className="rounded-md px-[9px] py-1.5 hover:!bg-primary-50">
                      <Icon
                        icon="attach-square-mono"
                        className="cursor-pointer"
                        htmlFor="file-upload"
                      />
                    </div>
                  </Tippy>

                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".txt,.pdf,.png,.jpg,.jpeg,.csv,.docx,.rtf"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".txt,.pdf,.png,.jpeg,.csv"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={isLoading || isGenerating}
                  />
                </div>
              )}
              <ComponentButtons onTagPreview={onTagPreview} onTagRemove={onTagRemove} />
            </div>
            <div className="flex">
              <div className="mr-1.5 flex h-[32px] w-[32px] items-center justify-center">
                <Transcribe
                  withLabel={false}
                  value={inputValue}
                  iconSize={22}
                  setValue={(text) =>
                    onTextChange({
                      target: { value: text, name }
                    })
                  }
                  disabled={false}
                  setDisable={() => {}}
                />
              </div>
              {(isLoading || isGenerating) && intelligenceChatId && (
                <Tippy content="Stop response" className="tippy-dark" placement="bottom">
                  <div className="flex h-[32px] w-[32px] items-center justify-center">
                    <Icon
                      icon="stop-circle-filled"
                      size={32}
                      color="primary"
                      onClick={onStopGeneration}
                    />
                  </div>
                </Tippy>
              )}
              {((!isLoading && !isGenerating) || !intelligenceChatId) && (
                <Button
                  icon="send-filled"
                  iconSize={18}
                  iconColor="white"
                  className={cs(
                    'mb-1 h-[32px] w-[32px] border-none',
                    disableGenerate ? '!bg-neutral-100' : 'bg-primary-500'
                  )}
                  onClick={handleGenerate}
                  disabled={disableGenerate}
                  type="secondary"
                  color={disableGenerate ? 'neutral' : 'white'}
                />
              )}
            </div>
          </div>
          {files.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {files.map((file) => (
                <div
                  key={file.name}
                  className="flex items-center gap-1 rounded bg-neutral-100 px-2 py-1">
                  <span className="text-sm text-neutral-700">{file.name}</span>
                  <Icon
                    icon="close-thick-linear"
                    size={16}
                    color="neutral"
                    onClick={() => handleRemoveFile(file.name)}
                    className="cursor-pointer"
                  />
                </div>
              ))}
            </div>
          )}
        </AIChatInput>
        <p className="text-center text-xs italic text-neutral-700">
          AI may make mistakes. Providers are responsible for the accuracy of all clinical
          documentation.
        </p>
        {showConfirm && (
          <Confirm
            handleOpen={() => setShowConfirm(true)}
            handleClose={() => setShowConfirm(false)}
            handleContinue={() => {
              setShowConfirm(false);
              onGenerate();
            }}
            title="Warning!"
            message="Heads up! You’ve entered fewer than 50 words. MyScribe is a trained medical scribe—working solely off of the information you provide. For the most accurate results, please add more detail and include outside context using the tools below your chat box."
            primaryBtnTxt="Yes"
            secondaryBtnTxt="No"
            icon="myscribeai-filled"
            variant="danger"
            footer={
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  text="Proceed anyway"
                  color="red"
                  shade="400"
                  onClick={() => {
                    setShowConfirm(false);
                    onGenerate();
                  }}
                />
                <Button
                  text="Add more info"
                  color="neutral"
                  outlined
                  onClick={() => setShowConfirm(false)}
                />
              </div>
            }
          />
        )}
      </>
    </div>
  );
}
